import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

/**
 * Options for the useKeyboardAwareScroll hook
 */
export interface KeyboardAwareScrollOptions {
  /**
   * Extra space (in pixels) - retained for backward compatibility but no longer used for auto-scrolling
   * @default 16
   */
  scrollPadding?: number;
  
  /**
   * Animation duration (in milliseconds) - retained for backward compatibility but no longer used
   * @default 300
   */
  scrollDuration?: number;
  
  /**
   * Callback to be executed when keyboard visibility changes
   */
  onKeyboardVisibilityChange?: (isVisible: boolean) => void;
}

/**
 * A hook that provides keyboard-aware functionality for input fields
 * 
 * This hook tracks keyboard visibility, measures keyboard height, and provides
 * a way to register input elements for focus tracking and keyboard dismissal.
 * 
 * @param options Configuration options for the hook
 * @returns An object containing:
 * - contentRef: Reference to be attached to scrollable content container
 * - keyboardHeight: Current height of the keyboard (0 when hidden)
 * - keyboardVisible: Boolean indicating if keyboard is currently visible
 * - registerInput: Function to register an input element ref for focus tracking
 * - hideKeyboard: Function to hide the keyboard
 * 
 * @example
 * ```typescript
 * // Get the hook values
 * const { 
 *   contentRef, 
 *   keyboardHeight, 
 *   keyboardVisible, 
 *   registerInput, 
 *   hideKeyboard 
 * } = useKeyboardAwareScroll({ scrollPadding: 20 });
 * 
 * // Usage in JSX (shown as plain text to avoid parsing issues)
 * // <div ref={contentRef}>
 * //   <input ref={inputRef} />
 * //   <button onClick={hideKeyboard}>Hide Keyboard</button>
 * // </div>
 * 
 * // Register an input
 * // Use useEffect to register the input when it's available
 * useEffect(() => {
 *   if (inputRef.current) {
 *     // This registers the input for focus tracking
 *     registerInput(inputRef);
 *   }
 * }, [registerInput, inputRef]);
 * ```
 */
export function useKeyboardAwareScroll(options: KeyboardAwareScrollOptions = {}) {
  const {
    scrollPadding = 16,
    scrollDuration = 300,
    onKeyboardVisibilityChange,
  } = options;
  
  // Reference to the scrollable content container (typically IonContent)
  const contentRef = useRef<HTMLElement>(null);
  
  // Track keyboard height and visibility
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  
  // Store references to input elements that need scrolling
  const inputRefs = useRef<Set<React.RefObject<HTMLElement>>>(new Set());
  
  // Track the currently focused input element
  const focusedInputRef = useRef<React.RefObject<HTMLElement> | null>(null);
  
  // Store last reported keyboard height to prevent duplicate adjustments
  const lastKnownKeyboardHeight = useRef<number>(0);
  
  // Flag to prevent keyboard event processing during manual dismissal
  const isManuallyDismissing = useRef<boolean>(false);
  
  // Flag to track if component is mounted
  const isMounted = useRef<boolean>(false);
  
  // Track initial viewport height to detect WebView resizing
  const initialViewportHeight = useRef<number>(0);
  
  // Get platform information for device-specific handling
  const platform = Capacitor.getPlatform();
  const isIOS = platform === 'ios';
  const isAndroid = platform === 'android';
  
  /**
   * Set keyboard visibility and height state
   */
  const setKeyboardState = useCallback((visible: boolean, height: number) => {
    // Log keyboard state for debugging
    
    // Use the raw keyboard height as reported by the device
    let adjustedHeight = height;
    
    // Android-specific handling: Always disable footer adjustment since Android always resizes WebView
    if (isAndroid) {
      if (visible && height > 0) {
        // On Android, WebView resizing is inconsistent but always happens
        // So we always disable footer adjustment to prevent double movement
        adjustedHeight = 0;
      }
    } else if (visible && height > 0) {
      // iOS-specific handling: Check for unexpected WebView resizing
      const currentViewportHeight = window.innerHeight;
      const viewportShrinkage = Math.max(0, initialViewportHeight.current - currentViewportHeight);
      
      
      if (viewportShrinkage > 50) {
        // iOS shouldn't resize with our config, but if it does, compensate
        adjustedHeight = Math.max(0, height - viewportShrinkage);
      }
    }
    
    // Avoid redundant updates if state is already correct
    if (visible === keyboardVisible && lastKnownKeyboardHeight.current === adjustedHeight) {
      return;
    }

    // Update state with the adjusted height
    setKeyboardHeight(adjustedHeight);
    setKeyboardVisible(visible);
    lastKnownKeyboardHeight.current = adjustedHeight; // Update last known height
    
    // Call the visibility change callback if provided
    if (onKeyboardVisibilityChange) {
      onKeyboardVisibilityChange(visible);
    }
  }, [onKeyboardVisibilityChange, keyboardVisible, platform, isAndroid, isIOS]);
  
  /**
   * Hide the keyboard programmatically
   */
  const hideKeyboard = useCallback(async () => {
    // Avoid attempting to hide if keyboard is not visible or dismissal is in progress
    if (!keyboardVisible || isManuallyDismissing.current) {
      return;
    }

    // Set flag to prevent event conflicts during dismissal
    isManuallyDismissing.current = true;

    // Blur any focused input elements
    if (focusedInputRef.current?.current && typeof focusedInputRef.current.current.blur === 'function') {
      focusedInputRef.current.current.blur();
    }
    if (document.activeElement instanceof HTMLElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    
    // Clear focused input reference
    focusedInputRef.current = null;

    // Hide the keyboard using Capacitor
    await Keyboard.hide();
    
    // Ensure state is updated
    setKeyboardState(false, 0);

    // Reset the manual dismissal flag after a delay
    setTimeout(() => {
      isManuallyDismissing.current = false;
    }, 400); // Delay to ensure events are processed properly

  }, [keyboardVisible, setKeyboardState]);
  
  // Auto scroll functionality removed per user request

  // Initialize Keyboard listeners on component mount
  useEffect(() => {
    isMounted.current = true;
    
    // Track viewport height changes to detect WebView resizing
    const currentViewportHeight = window.innerHeight;
    initialViewportHeight.current = currentViewportHeight;
    
    const handleKeyboardWillShow = (info: KeyboardInfo) => {
      if (!isMounted.current) return;
      
      // Ignore keyboard show events during manual dismissal
      if (isManuallyDismissing.current) {
        return;
      }
      
      if (info.keyboardHeight > 0) {
        // Log viewport changes to detect WebView resizing
        const currentViewportHeight = window.innerHeight;
        const viewportChange = initialViewportHeight.current - currentViewportHeight;
      
        
        setKeyboardState(true, info.keyboardHeight);
        
        // Auto scroll functionality removed per user request
      }
    };
    
    const handleKeyboardWillHide = () => {
      if (!isMounted.current) return;
      
      // Log viewport changes on hide
      const currentViewportHeight = window.innerHeight;
      
      // Reset initial viewport height for next keyboard event
      // This ensures accurate viewport change detection on subsequent keyboard openings
      setTimeout(() => {
        if (isMounted.current) {
          const resetViewportHeight = window.innerHeight;
          initialViewportHeight.current = resetViewportHeight;
        }
      }, 100); // Small delay to ensure viewport has stabilized
      
      // Only update state for native hide events (not during manual dismissal)
      if (!isManuallyDismissing.current) {
        setKeyboardState(false, 0);
        focusedInputRef.current = null;
      }
    };
    
    let keyboardWillShowListener: any = null;
    let keyboardWillHideListener: any = null;
    
    const setupListeners = async () => {
      try {
        keyboardWillShowListener = await Keyboard.addListener('keyboardWillShow', handleKeyboardWillShow);
        keyboardWillHideListener = await Keyboard.addListener('keyboardWillHide', handleKeyboardWillHide);
      } catch (error) {
        console.error('[useKeyboardAwareScroll] Error setting up keyboard listeners:', error);
      }
    };
    
    setupListeners();
    
    return () => {
      isMounted.current = false;
      if (keyboardWillShowListener) keyboardWillShowListener.remove();
      if (keyboardWillHideListener) keyboardWillHideListener.remove();
    };
  }, [setKeyboardState]);
  
  /**
   * Register an input element for scroll handling
   */
  const registerInput = useCallback((inputRef: React.RefObject<HTMLElement>) => {
    if (!inputRef?.current) return;
    
    inputRefs.current.add(inputRef);
    const inputElement = inputRef.current;

        const onFocusListener = () => {
      if (!isMounted.current) return;
      
      focusedInputRef.current = inputRef;
      
      // Auto scroll functionality removed per user request
    };
    
    const onBlurListener = () => {
      if (!isMounted.current) return;
      
      // If we're not in a manual dismissal, check if focus moved to another element
      if (!isManuallyDismissing.current) {
        setTimeout(() => {
          if (isMounted.current && focusedInputRef.current === inputRef && document.activeElement !== inputElement) {
            // Check if activeElement is another registered input
            let isFocusOnAnotherRegisteredInput = false;
            if (document.activeElement) {
              for (const ref of inputRefs.current) {
                if (ref.current === document.activeElement) {
                  isFocusOnAnotherRegisteredInput = true;
                  break;
                }
              }
            }
            
            // Clear focusedInputRef if focus didn't move to another registered input
            if (!isFocusOnAnotherRegisteredInput) {
              focusedInputRef.current = null;
            }
          }
        }, 100);
      }
    };
    
    inputElement.addEventListener('focus', onFocusListener);
    inputElement.addEventListener('blur', onBlurListener);
    
        // If this input is already focused when registered
    if (document.activeElement === inputElement) {
      focusedInputRef.current = inputRef;
      // Auto scroll functionality removed per user request
    }
    
    // Return cleanup function
    return () => {
      inputRefs.current.delete(inputRef);
      if (inputElement) {
        inputElement.removeEventListener('focus', onFocusListener);
        inputElement.removeEventListener('blur', onBlurListener);
      }
      if (focusedInputRef.current === inputRef) {
        focusedInputRef.current = null;
      }
    };
  }, [keyboardVisible, keyboardHeight]);
  
  return {
    contentRef,
    keyboardHeight,
    keyboardVisible,
    registerInput,
    hideKeyboard,
  };
}

export default useKeyboardAwareScroll; 